import './index.css';
import 'assets/scss/style.scss';

import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/carousel/styles.css';

import './styles/responsive.css';

import { forwardRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { Notifications } from '@mantine/notifications';
import { IconAlertCircle, IconAlertTriangle, IconCircleCheck, IconInfoCircle } from '@tabler/icons-react';
import ThemeProvider from './theme/ThemeProvider';
import { useThemeMode } from './theme/useThemeMode';
import { semanticColors, semanticPalette } from './theme/semanticColors';
import { radius } from './theme/radius';
import { shadows } from './theme/shadows';

import { MaterialDesignContent, SnackbarProvider } from 'notistack';

import Routes from './routes';

import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import useMetaPixel from 'hooks/use-meta-pixel';

import clearReduxStore from 'utils/clear-redux-store';
import { store } from './store';
import { setLoggedOut } from 'store/features/userSlice';

const stripePromise = loadStripe(import.meta.env.VITE_APP_STRIPE_PUBLISHABLE_KEY);

const stripeElementOptionsByMode = {
  dark: {
    appearance: {
      theme: 'night',
      variables: {
        fontFamily: 'Sohne, system-ui, sans-serif',
        fontWeightNormal: '500',
        borderRadius: '8px',
        colorBackground: '#0A2540',
        colorPrimary: '#EFC078',
        accessibleColorOnColorPrimary: '#1A1B25',
        colorText: 'white',
        colorTextSecondary: 'white',
        colorTextPlaceholder: '#ABB2BF',
        tabIconColor: 'white',
        logoColor: 'dark'
      },
      rules: {
        '.Input': {
          backgroundColor: '#212D63',
          border: '1px solid var(--colorPrimary)'
        }
      },
      layout: {
        type: 'tabs',
        defaultCollapsed: false
      }
    }
  },
  light: {
    appearance: {
      theme: 'stripe',
      variables: {
        fontFamily: 'Sohne, system-ui, sans-serif',
        fontWeightNormal: '500',
        borderRadius: '8px',
        colorBackground: semanticPalette.light.surface.base,
        colorPrimary: '#f05776',
        colorText: semanticPalette.light.text.primary,
        colorTextSecondary: semanticPalette.light.text.secondary,
        colorTextPlaceholder: semanticPalette.light.text.muted
      },
      rules: {
        '.Input': {
          backgroundColor: semanticPalette.light.surface.raised,
          border: `1px solid ${semanticPalette.light.border.default}`
        }
      },
      layout: {
        type: 'tabs',
        defaultCollapsed: false
      }
    }
  }
};

// Stripe Elements appearance follows the color scheme; must render inside
// ThemeProvider to read the mode.
const StripeElementsProvider = ({ children }) => {
  const { mode } = useThemeMode();
  const options = useMemo(() => stripeElementOptionsByMode[mode], [mode]);

  return (
    <Elements stripe={stripePromise} options={options}>
      {children}
    </Elements>
  );
};

StripeElementsProvider.propTypes = {
  children: PropTypes.node
};

// notistack, themed once: every enqueueSnackbar in the app renders through
// these. A toast is the raised surface washed with its status tint (laid over
// the surface, so the page never shows through), a full 1px border in the
// status color and a status icon. The message stays text.primary, well over
// 4.5:1 on every tint in both schemes; a saturated fill could not promise that
// (white on status.error is 4.4:1 in light). No side stripes (DESIGN.md).
const SNACKBAR_BASE_STYLE = {
  backgroundColor: semanticColors.surface.raised,
  color: semanticColors.text.primary,
  borderRadius: radius.surface,
  boxShadow: shadows.popover,
  padding: '6px 16px 6px 14px'
};

const tinted = (tint, border) => ({ backgroundImage: `linear-gradient(${tint}, ${tint})`, border: `1px solid ${border}` });

const SNACKBAR_STYLE_BY_VARIANT = {
  default: { ...SNACKBAR_BASE_STYLE, border: `1px solid ${semanticColors.border.default}` },
  success: { ...SNACKBAR_BASE_STYLE, ...tinted(semanticColors.status.successBg, semanticColors.status.success) },
  error: { ...SNACKBAR_BASE_STYLE, ...tinted(semanticColors.status.errorBg, semanticColors.status.error) },
  warning: { ...SNACKBAR_BASE_STYLE, ...tinted(semanticColors.status.warningBg, semanticColors.status.warning) },
  info: { ...SNACKBAR_BASE_STYLE, ...tinted(semanticColors.status.infoBg, semanticColors.status.info) }
};

const snackbarIconStyle = (color) => ({ color, flexShrink: 0, marginInlineEnd: 10 });

// Replaces notistack's Material icons; `default` has none, as before.
const SNACKBAR_ICONS = {
  success: <IconCircleCheck size={20} stroke={1.75} aria-hidden style={snackbarIconStyle(semanticColors.status.success)} />,
  error: <IconAlertCircle size={20} stroke={1.75} aria-hidden style={snackbarIconStyle(semanticColors.status.error)} />,
  warning: <IconAlertTriangle size={20} stroke={1.75} aria-hidden style={snackbarIconStyle(semanticColors.status.warning)} />,
  info: <IconInfoCircle size={20} stroke={1.75} aria-hidden style={snackbarIconStyle(semanticColors.status.info)} />
};

// notistack's own content (role="alert", icon, message, action) with the
// tokens on top. Inline style beats its generated classes; the transition's
// style is spread last so the slide keeps working.
const ThemedSnackbar = forwardRef(({ variant, style, ...props }, ref) => (
  <MaterialDesignContent ref={ref} variant={variant} style={{ ...SNACKBAR_STYLE_BY_VARIANT[variant], ...style }} {...props} />
));

ThemedSnackbar.displayName = 'ThemedSnackbar';
ThemedSnackbar.propTypes = {
  variant: PropTypes.string.isRequired,
  style: PropTypes.object
};

const SNACKBAR_COMPONENTS = Object.fromEntries(Object.keys(SNACKBAR_STYLE_BY_VARIANT).map((variant) => [variant, ThemedSnackbar]));

// eslint-disable-next-line arrow-body-style
const App = () => {
  useMetaPixel('1042248837247384');

  // Cross-tab logout: when another tab sets isLoggedIn to 'false', force logout in this tab
  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === 'isLoggedIn' && event.newValue === 'false') {
        store.dispatch(setLoggedOut());
        clearReduxStore();
        sessionStorage.clear();
        window.location.assign('/login');
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  return (
    <ThemeProvider>
      <GoogleOAuthProvider clientId={import.meta.env.VITE_APP_AUTH_GOOGLE_CLIENT_ID}>
        <Notifications position="top-right" zIndex={1700} />
        <SnackbarProvider maxSnack={3} Components={SNACKBAR_COMPONENTS} iconVariant={SNACKBAR_ICONS}>
          <StripeElementsProvider>
            <Routes />
          </StripeElementsProvider>
        </SnackbarProvider>
      </GoogleOAuthProvider>
    </ThemeProvider>
  );
};

export default App;
